import React from 'react';
import { useServiceInvestigation } from '../../../hooks';
import { ServiceType } from '../../../pages/hospital/Request/types';
import SurgicalProcedureSelectorView, {
  SurgicalProcedureOption,
} from './SurgicalProcedureSelectorView';
import { SurgicalProcedureValue, parseSurgicalProcedureName } from '../../../utils/surgicalProcedure';

interface SurgicalProcedureSelectorProps {
  /** redux-form Field input ({ value, onChange, name }). */
  input: any;
  /** Field label (already translated upstream). */
  label?: React.ReactNode;
  /** Validation error state. */
  error?: boolean;
}

/**
 * SHER-459 — container for the operative report "Geste principal" field.
 *
 * Sources its options from the live surgical procedures catalog (the same
 * SURGERY-type services used by "Ajouter un acte chirurgical"), so the dropdown
 * stays in sync with the catalogue instead of carrying frozen placeholder
 * options. It stores a `{ procedure, procedureId }` object (mirroring the ICD
 * field's name+code shape): the procedure name drives every display/PDF, while
 * the catalog id is retained for statistics.
 */
const SurgicalProcedureSelector: React.FC<SurgicalProcedureSelectorProps> = ({ input, label, error }) => {
  const { services, loadingServices } = useServiceInvestigation(ServiceType.SURGERY);

  const options: SurgicalProcedureOption[] = (services || [])
    .filter((service: any) => service && service.description)
    .map((service: any) => ({ value: service.description, label: service.description }));

  const currentName = parseSurgicalProcedureName(input.value);

  function handleChange(procedureName: string) {
    if (!procedureName) {
      input.onChange('');
      return;
    }
    const match = (services || []).find((service: any) => service && service.description === procedureName);
    const value: SurgicalProcedureValue = {
      procedure: procedureName,
      procedureId: match ? match.id : null,
    };
    input.onChange(value);
  }

  return (
    <SurgicalProcedureSelectorView
      value={currentName}
      options={options}
      loading={loadingServices}
      label={label}
      error={error}
      onChange={handleChange}
    />
  );
};

export default SurgicalProcedureSelector;
